import { isNil, isObject, merge } from 'lodash-es';
import dayjs from 'dayjs';
import { ReportAction } from '../../api/operations';
import store from '../../store';
import { addAction, clearActions, uploadAll } from './upload';
import { stringify } from './helpers';
import Cache from './cache';
import { ReportHubProps, ReportActionProps } from '../../types';

import type Plugin from './plugins';
import ReportPlugin from './plugins/report';
import VisualizationPlugin from './plugins/visualization';
import PageViewPlugin from './plugins/page-view';
import PageDurationPlugin from './plugins/page-duration';
import PageRerenderPlugin from './plugins/page-rerender';

const actionCache = new Cache(500);

class Report {
  // 用户唯一标识
  public uin = '';
  // 自定义公共参数
  public commonParams: Record<string, any> = {};
  // 上报开关
  private reportSwitch = true;
  // 插件
  private plugins: Array<Plugin> = [];

  constructor(config: ReportHubProps) {
    const { appId } = config;
    if (!appId) {
      console.warn('Data-report-hub initialization failed: appId is required');
      return;
    }
    store.setAppId(appId);
    store.setConfig({
      enableReport: true,
      enableVisualization: true,
      spa: true,
      include: [],
      exclude: [],
      enableDefaultReport: true,
      retryTimes: 3,
      ...config,
    });

    this.plugins.push(new VisualizationPlugin(this));
    this.plugins.push(new PageViewPlugin(this));
    this.plugins.push(new PageDurationPlugin(this));
    this.plugins.push(new PageRerenderPlugin(this));
    this.plugins.push(new ReportPlugin(this));
  }

  /**
   * 设置是否开启可视化
   * @param params 自定义参数
   */
  setEnableVisualization = (enable: boolean) => {
    (this.getPlugin('visualization') as VisualizationPlugin)?.setEnable?.(enable);
  };

  /**
   * 设置公共参数
   * @param params 自定义参数
   */
  setCommonInfo = (params: Record<string, any> = {}) => {
    const { uin, ...other } = params;
    if (uin) {
      this.dispatchEvent('UinChange', { uin, oldUin: this.uin });
      this.uin = String(uin);
    }
    this.commonParams = merge({}, this.commonParams, other || {});
  };

  /**
   * 开启上报
   */
  reportSwitchOn = () => {
    this.reportSwitch = true;
  };

  /**
   * 关闭上报
   */
  reportSwitchOff = () => {
    this.reportAll();
    this.reportSwitch = false;
  };

  /**
   * 事件上报
   * @param params 上报参数
   */
  reportAction = (params: string | ReportActionProps) => {
    if (!store.config.enableReport || !this.reportSwitch) return;

    // 传入参数解析
    let fields: any = { action: '' };
    if (typeof params === 'string') {
      fields = { action: params };
    } else if (isObject(params)) {
      fields = params;
    }

    // 上报参数组装
    const { action, is_global, path: pathname, duration, ...other }: any = merge({}, this.commonParams, fields);

    let data: ReportAction = {
      uin: this.uin,
      time: dayjs().format('YYYY-MM-DD HH:mm:ss'),
      path: pathname || store.getPathname(),
      action,
      is_global,
      duration,
      other_fields: Object.keys(other)
        .filter(field => !isNil(other[field]))
        .map(field => ({
          field,
          value: stringify(other[field]),
        })),
    };

    // 避免重复上报
    if (actionCache.has(`${action}-${data.path}`)) return;
    actionCache.put(`${action}-${data.path}`);

    // 触发页面改变事件
    if (action === 'page-view') {
      this.dispatchEvent('PageChange');
    }

    // 上报前钩子，可修改或阻止上报
    if (typeof store.config.beforeUpload === 'function') {
      const res = store.config.beforeUpload.call(null, data);
      if (res === false) return; // 阻止上报
      if (isObject(res)) {
        data = res as ReportAction;
      }
    }

    // 加入上报队列
    if (!data.uin) {
      console.warn('Data-report-hub report failed: uin is required, please set uin with method setCommonInfo');
      return;
    };
    if (!data.action) {
      console.warn('Data-report-hub report failed: action is required');
      return;
    };
    addAction(data);
  };

  /**
   * 立刻上报全部数据
   */
  reportAll = () => {
    (this.getPlugin('pageDuration') as PageDurationPlugin).reportDuration();
    (this.getPlugin('pageDuration') as PageDurationPlugin).clearDuration();
    uploadAll();
  };

  /**
   * 清除上报数据
   */
  clearAll = () => {
    (this.getPlugin('pageDuration') as PageDurationPlugin).clearDuration();
    clearActions();
  };

  /**
   * 渲染页面热点
   */
  renderHottag = () => { };

  /**
   * 按名称获取插件
   * @param name 插件名称
   * @returns 插件
   */
  private getPlugin(name: string) {
    return this.plugins.find(v => v.name === name);
  }

  /**
   * 触发插件事件
   */
  private dispatchEvent(event: string, params?: Record<string, any>) {
    this.plugins.forEach(v => v.listen(event, params));
  }
}

export default Report;
